import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Share2 } from "lucide-react";

import { Button } from "./ui/button";
import { copyText, shareText } from "../lib/shareText";

/**
 * One-tap share into WhatsApp (or wherever the OS offers).
 *
 * `getText` is a function rather than a string so the summary is built from
 * current state at the moment of tapping — sharing a stale plan is worse than
 * not sharing at all.
 *
 * Falls back to the clipboard on desktop browsers with no share sheet, so the
 * button always does something useful rather than silently failing.
 */
export function ShareButton({
  getText,
  title = "Share",
  label = "Share",
  variant = "outline",
  size = "sm",
  testId = "share-btn",
}) {
  const [copied, setCopied] = useState(false);

  const handle = async () => {
    const text = getText?.() || "";
    if (!text.trim()) {
      toast.error("Nothing to share yet");
      return;
    }
    const result = await shareText(text, { title });
    if (result === "cancelled") return;
    if (result === "unavailable") {
      const ok = await copyText(text);
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("Copied — paste it into your group");
      } else {
        toast.error("Could not share on this device");
      }
    }
  };

  return (
    <Button variant={variant} size={size} onClick={handle} className="gap-1.5"
            data-testid={testId}>
      {copied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
      {label}
    </Button>
  );
}

/** Copy-only variant, for places where a share sheet would be the wrong gesture. */
export function CopyButton({ getText, label = "Copy", testId = "copy-btn" }) {
  const [copied, setCopied] = useState(false);

  const handle = async () => {
    const text = getText?.() || "";
    if (!text.trim()) return;
    if (await copyText(text)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied");
    } else {
      toast.error("Could not copy");
    }
  };

  return (
    <Button variant="outline" size="sm" onClick={handle} className="gap-1.5"
            data-testid={testId}>
      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {label}
    </Button>
  );
}

export default ShareButton;
